"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { usePublicContext } from "../../context/PublicContext";
import { Subcategory } from "../../types/public.types";

export default function AgriInputsSubcat() {
  const router = useRouter();
  const { subcategories: rawSubcategories, loadingSubcategories: isLoading, categories } = usePublicContext();
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  // Filter subcategories for "Agri Inputs"
  useEffect(() => {
    if (rawSubcategories) {
      const filtered = rawSubcategories.filter((sub) => {
        if (!sub.isActive) return false;
        const cat = sub.category;
        if (typeof cat === "string") {
          const categoryObj = categories.find((c) => c._id === cat);
          return categoryObj && (
            categoryObj.slug === "agri-inputs" ||
            categoryObj.name.toLowerCase().includes("input")
          );
        } else if (cat && typeof cat === "object") {
          return (
            cat.slug === "agri-inputs" ||
            cat.name.toLowerCase().includes("input")
          );
        }
        return false;
      });
      setSubcategories(filtered);
    }
  }, [rawSubcategories, categories]);

  const frameIdRef = useRef<number | null>(null);

  const checkScrollPosition = () => {
    if (frameIdRef.current !== null) return;

    frameIdRef.current = requestAnimationFrame(() => {
      frameIdRef.current = null;
      const container = scrollContainerRef.current;
      if (container) {
        const { scrollLeft, scrollWidth, clientWidth } = container;
        const nextLeft = scrollLeft > 5;
        const nextRight = scrollLeft + clientWidth < scrollWidth - 5;

        setShowLeftArrow(nextLeft);
        setShowRightArrow(nextRight);
      }
    });
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    let initialTimeoutId: any = null;

    if (container) {
      container.addEventListener("scroll", checkScrollPosition, { passive: true });
      initialTimeoutId = setTimeout(() => {
        checkScrollPosition();
      }, 100);
    }

    let resizeObserver: ResizeObserver | null = null;
    if (container && typeof window !== "undefined" && "ResizeObserver" in window) {
      resizeObserver = new ResizeObserver(() => {
        checkScrollPosition();
      });
      resizeObserver.observe(container);
    } else {
      window.addEventListener("resize", checkScrollPosition, { passive: true });
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", checkScrollPosition);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener("resize", checkScrollPosition);
      }
      if (initialTimeoutId) {
        clearTimeout(initialTimeoutId);
      }
      if (frameIdRef.current !== null) {
        cancelAnimationFrame(frameIdRef.current);
      }
    };
  }, [subcategories]);

  const scroll = (direction: "left" | "right") => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = container.clientWidth * 0.75;
      container.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  if (isLoading) {
    return (
      <section className="py-10 relative bg-white overflow-hidden border-t border-slate-100 min-h-[400px]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-left max-w-2xl mb-8">
            <div className="h-8 w-64 bg-slate-200 animate-pulse rounded mb-3" />
            <div className="h-4 w-96 bg-slate-100 animate-pulse rounded" />
          </div>
          <div className="flex gap-6 overflow-x-hidden pb-8 pt-2">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="flex-none w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] bg-white rounded-lg border border-slate-200/80 p-4 h-[320px] animate-pulse"
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (subcategories.length === 0) {
    return null; // Don't show the section if no subcategories exist
  }

  return (
    <section className="py-10 relative bg-white overflow-hidden border-t border-slate-100 min-h-[400px]">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

        {/* Section Header */}
        <div className="text-left mb-8 flex justify-between items-end border-b border-gray-100 pb-4">
          <div>
            <h2 className="text-2xl font-extrabold text-green-600 tracking-tight capitalize">
              Explore Agri-Input Categories            </h2>
            <p className="mt-2 text-sm text-slate-500 font-medium max-w-2xl">
              Explore agricultural inputs across crop nutrients, crop protection, farm machinery, agricultural equipment, irrigation, tools and other products required across the farming value chain.            </p>
          </div>
          <button
            onClick={() => router.push("/agri-inputs")}
            className="text-xs font-bold text-green-700 hover:text-green-800 hover:underline shrink-0 pb-1"
          >
            View All &rarr;
          </button>
        </div>

        {/* Carousel Viewport Wrapper with Floating Arrows */}
        <div className="relative group/carousel -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">

          {/* Floating Left Arrow */}
          {showLeftArrow && (
            <button
              onClick={() => scroll("left")}
              className="hidden sm:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 bg-white hover:bg-green-50 text-slate-850 p-2.5 rounded-full border border-slate-200 shadow-md hover:scale-105 transition-all active:scale-95 cursor-pointer"
              aria-label="Scroll left"
            >
              <ChevronLeft className="w-5 h-5 text-green-700" />
            </button>
          )}

          {/* Floating Right Arrow */}
          {showRightArrow && (
            <button
              onClick={() => scroll("right")}
              className="hidden sm:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 bg-white hover:bg-green-50 text-slate-850 p-2.5 rounded-full border border-slate-200 shadow-md hover:scale-105 transition-all active:scale-95 cursor-pointer"
              aria-label="Scroll right"
            >
              <ChevronRight className="w-5 h-5 text-green-700" />
            </button>
          )}

          {/* Scroll Container */}
          <div
            ref={scrollContainerRef}
            className="flex gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-8 pt-2 no-scrollbar"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            {subcategories.map((sub) => {
              const categorySlug =
                typeof sub.category === "object" && sub.category !== null
                  ? (sub.category as any).slug || "general"
                  : "general";
              const cardUrl = `/${categorySlug}/${sub.slug}`;

              return (
                <div
                  key={sub._id}
                  className="flex-none w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] snap-start"
                >
                  <div
                    onClick={() => router.push(cardUrl)}
                    className="group bg-white rounded-md border border-slate-200 transition-all duration-305 hover:shadow-lg hover:border-green-300 hover:-translate-y-1 flex flex-col justify-between p-4 h-[350px] overflow-hidden cursor-pointer select-none"
                  >

                    <div className="flex flex-col flex-1">
                      {/* Rounded Image Frame with Fixed Height */}
                      <div className="relative w-full h-[150px] rounded-md overflow-hidden bg-slate-50 border border-slate-100 flex items-center justify-center">
                        {sub.imageUrl ? (
                          <Image
                            src={sub.imageUrl}
                            alt={sub.name}
                            fill
                            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                            className="object-cover rounded-md transition-transform duration-500 group-hover:scale-105"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-green-50 to-emerald-100/50 flex items-center justify-center rounded-md">
                            <span className="text-3xl font-black text-green-700/20 tracking-wider">
                              {sub.name.slice(0, 2).toUpperCase()}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Content Area */}
                      <div className="flex flex-col mt-3">
                        {/* Subcategory Title */}
                        <h3 className="text-lg font-bold text-slate-900 capitalize line-clamp-1 group-hover:text-green-600 transition-colors duration-200 h-[28px] flex items-center">
                          {sub.name}
                        </h3>

                        {/* Description */}
                        <div className="h-[40px] overflow-hidden mt-1.5">
                          <p className="text-slate-500 text-sm font-medium line-clamp-2 leading-relaxed">
                            {sub.description || `Browse quality ${sub.name.toLowerCase()} listed on our platform.`}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Explore Link */}
                    <div className="mt-auto pt-2 flex justify-between items-center">
                      <span className="inline-flex items-center gap-1.5 text-sm font-extrabold text-green-700 group-hover:text-green-800 transition-colors duration-200">
                        Explore More &rarr;
                      </span>
                    </div>

                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
